
































import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { CrudService } from "../core/services/crudService";
import { ShopService } from "../core/services/shopService";
import { Settings, SettingsService } from "../core/services/configService";
import { CustomerHelper } from "../crm/helpers/customerHelper";
import { ProgramPackTexts } from "./programPackTexts";
import ProgramPage from "../components/programs/ProgramPage.vue";
import ProgramMeta from "../components/programs/ProgramMeta.vue";
import ProgramPanel from "../components/programs/ProgramPanel.vue";
import ProgramColumns from "../components/programs/ProgramColumns.vue";
import ProgramReports from "../components/programs/ProgramReports.vue";
import ProgramEvolution from "../components/programs/ProgramEvolution.vue";
import CzCard from "../components/cz/CzCard.vue";
import CzTitle from "../components/cz/CzTitle.vue";
import ProgramPackHead from "../components/programs/ProgramPackHead.vue";
import CzSegmented from "../components/cz/CzSegmented.vue";
import CzNotice from "../components/cz/CzNotice.vue";
import CzButton from "../components/cz/CzButton.vue";
import CzLink from "../components/cz/CzLink.vue";
import store from "../core/store";
import { GuidelineApi, CardGuideline, ScopeProgram } from "../components/guidelines/guidelineApi";

@Component({
  components: { ProgramPage, ProgramMeta, ProgramPanel, ProgramColumns, ProgramReports, ProgramEvolution, CzCard, CzTitle, CzSegmented, ProgramPackHead, CzNotice, CzButton, CzLink },
})
export default class MyProgramPackPage extends Vue {
  @Prop({ type: Number, default: 0 }) enrollmentId!: number;
  @Prop({ type: Boolean, default: false }) embedded!: boolean;
  enrollment: any = null;
  reports: any[] = [];
  loaded: boolean = false;
  tab: string = "plan";
  hasEvolution: boolean = false;
  errorMsg: string = "";
  texts = ProgramPackTexts;
  guidelines: { [id: number]: any } = {};
  today: string = "";
  busyId: number = 0;

  get emptyText(): string {
    return this.loaded && this.enrollment === null ? this.$t("This program is not available").toString() : "";
  }

  get id(): number {
    return this.enrollmentId || Number(this.$route.params.id);
  }

  get warningSigns(): string[] {
    return (this.enrollment.warningSigns || "").split("\n").map((x: string) => x.trim()).filter((x: string) => x);
  }

  get phone(): string {
    return store.state.settings && store.state.settings.Phone ? store.state.settings.Phone : "";
  }

  get tabs(): any[] {
    return [
      { value: "plan", label: this.t("Plan"), show: true },
      { value: "payments", label: this.t("Payments"), show: !!(this.enrollment.payment && this.enrollment.payment.installments.length) },
      { value: "evolution", label: this.t("Your evolution"), show: this.hasEvolution },
      { value: "reports", label: this.t("Reports"), show: this.reports.length > 0 },
    ].filter((tab) => tab.show);
  }

  get terms(): string[] {
    return this.texts.enrollmentFacts(this.t, this.enrollment).slice(0, -1).concat(this.texts.cancellation(this.t, this.enrollment));
  }

  // A guideline is one more row of its phase; the one for the whole program is painted in every phase and marked once.
  get columns(): any[] {
    const wholeProgram = this.enrollment.columns.reduce((all: any[], col: any) =>
      all.concat(col.cards.filter((k: any) => k.cardType === CardGuideline && k.guidelineScope === ScopeProgram)), []);
    return this.enrollment.columns.map((col: any, i: number) => {
      const own = col.cards.filter((k: any) => k.cardType === CardGuideline && k.guidelineScope !== ScopeProgram);
      const cards = own.concat(wholeProgram);
      return {
        name: col.name,
        summary: this.phaseSummary(col, i),
        rows: col.cards
          .filter((k: any) => k.cardType === 0)
          .reduce((rows: any[], k: any) => rows.concat(ProgramPackTexts.sessionRows(this.t, this.enrollment, k)), [])
          .map((r: any) => Object.assign(r, { icon: "calendar" }))
          .concat(cards.filter((k: any) => !this.guidelines[k.customerGuidelineId]).map((k: any) => this.pendingGuideline(k))),
        guidelines: cards.filter((k: any) => this.guidelines[k.customerGuidelineId]).map((k: any) => ({
          row: this.guidelines[k.customerGuidelineId],
          to: this.embedded
            ? { path: "/my-plan", query: { program: String(this.enrollment.id), routine: String(k.customerGuidelineId) } }
            : `/guidelines/${k.customerGuidelineId}?back=/customer-programs/${this.enrollment.id}`,
        })),
      };
    });
  }

  get currentPhase(): number {
    return ProgramPackTexts.currentPhase(this.enrollment, this.guidelines);
  }

  phaseSummary(col: any, i: number): string {
    if (i < this.currentPhase || col.cards.every((k: any) => ProgramPackTexts.cardDone(k, this.guidelines))) return this.t("Completed");
    if (i > this.currentPhase) return this.t("Not started yet");
    return this.t("In progress");
  }


  pendingGuideline(k: any): any {
    const detail = k.customerGuidelineId
      ? this.$t("finished").toString()
      : k.guidelineScope === ScopeProgram ? this.$t("starts with the program").toString() : this.$t("starts with its phase").toString();
    return { name: k.name, note: this.$t("Guideline").toString(), detail: detail, icon: k.customerGuidelineId ? "" : "lock" };
  }

  async loadGuidelines() {
    const data = await GuidelineApi.list();
    if (!data || !data.ok) return;
    this.today = GuidelineApi.day(data.today);
    const byId: { [id: number]: any } = {};
    data.data.forEach((row: any) => (byId[row.id] = row));
    this.guidelines = byId;
  }

  // A by-days program in progress with appointments still to book is booked whole on one day, as when it was bought.
  get canBookDay(): boolean {
    const e = this.enrollment;
    return e.layout === 0 && e.status === 2 && e.columns.some((col: any) => col.cards.some((k: any) => k.cardType === 0 && k.bookings.length < Math.max(1, k.quantity)));
  }

  get items(): string[] {
    return ProgramPackTexts.itemNames(this.enrollment);
  }

  get paymentSummary(): string {
    const p = this.enrollment.payment;
    return this.t("{0} paid of {1}").replace("{0}", ProgramPackTexts.money(p.totalPaid)).replace("{1}", ProgramPackTexts.money(p.totalWithDiscount));
  }

  // One row per installment; what is due can be paid from here (the checkout charges exactly that), the rest is
  // charged to the saved card on its day.
  get paymentColumns(): any[] {
    const p = this.enrollment.payment;
    return [{
      name: "",
      rows: p.installments.map((i: any) => ({
        name: this.t("Installment {0} of {1}").replace("{0}", String(i.number)).replace("{1}", String(p.installments.length)),
        note: ProgramPackTexts.money(i.amount),
        done: i.covered,
        detail: i.covered ? this.t("paid") : this.t(i.overdue ? "overdue since {0}" : "due on {0}").replace("{0}", ProgramPackTexts.date(i.dueDate)),
        action: i.due && p.dueNow > 0 ? { label: this.t("Pay"), pay: true } : null,
      })),
    }];
  }

  // The sale goes to the cart for what is due now, the amount the checkout will charge.
  pay() {
    const p = this.enrollment.payment;
    const sale = { id: p.saleId, title: this.enrollment.name, description: this.enrollment.name, totalWithDiscount: p.totalWithDiscount, totalPaid: p.totalPaid, pending: p.dueNow, customerId: Number(CustomerHelper.getStoredCustomer()) };
    new ShopService().addSaleToCart(sale, null);
    this.$router.push("/checkout");
  }

  t(key: string): string {
    return this.$t(key).toString();
  }

  // The same public cancellation as "Your appointments": the unit goes back to pending and the row offers "Book" again.
  async cancel(action: any) {
    this.errorMsg = "";
    const data = await new CrudService().httpPost(`${Settings.HostName}/api/publicbookings/cancel/${action.bookingId}/${CustomerHelper.getStoredCustomer()}`);
    if (!data || data.ok === false) {
      this.errorMsg = data && data.errorMsg ? data.errorMsg : this.$t("Crash").toString();
      return;
    }
    await this.load();
  }

  async load() {
    const url = `${Settings.HostName}/api/publicprogrampacks/customer/${CustomerHelper.getStoredCustomer()}/${this.id}`;
    const data = await new CrudService().httpGet(url);
    this.enrollment = data && data.ok ? data.model : null;
    this.loaded = true;
  }

  async mounted() {
    await Promise.all([this.load(), this.loadGuidelines()]);
    this.$nextTick(() => this.$emit("loaded"));
    if (this.enrollment) {
      if (!this.embedded) new SettingsService().setPageTitle(this.enrollment.name);
      const sent = await new CrudService().httpGet(`${Settings.HostName}/api/publicreports/customer/${CustomerHelper.getStoredCustomer()}`);
      this.reports = sent && sent.ok ? sent.data.filter((r: any) => r.customerProgramPackId === this.enrollment.id) : [];
    }
  }
}
